/**
 * Intake Builder store (Build tab) — ISOLATED, own state. Holds the card board and
 * the run log (Journal optimistic source). case_events persistence + press-play
 * replay are Increment B/C (see journalSpine.ts + CaseReplay). Spec:
 * docs/research/FUSION_INTAKE_BUILDER_SPEC.md.
 */
import { create } from 'zustand';

/** A run of a card = an EVENT (spec §2 — runs are events, not state). Journal reads these. */
export interface RunEvent {
  id: string;
  ts: number;
  cardId: string;
  registryId: string;
  title: string;
  summary: string;
  tier?: 'deterministic' | 'scored' | null;
  targetNodeId?: string;
  nodeLabel?: string;                  // produced node's label — lets replay match after a reload (id → canonical_id)
}

/** One frame of the Journal press-play replay (Increment C). Chronological (oldest first). */
export interface ReplayEntry {
  id: string;
  ts: number;
  title: string;
  summary: string;
  tier?: string | null;
  targetNodeId?: string;
  nodeLabel?: string;
  /** Demo seed row — Journal badge only; ignored by canvas replay. */
  synthetic?: boolean;
  /** Optimistic / outbox row not yet confirmed by case_events. */
  pending?: boolean;
}

export interface ReplayState {
  events: ReplayEntry[];               // oldest → newest
  step: number;                        // 1-based
  playing: boolean;
}

/** A cytoscape node `data` bag queued while the canvas is unmounted (e.g. authoring on the Build tab). */
export type PendingNode = Record<string, unknown> & { id: string };

export interface IntakeCard {
  id: string;
  registryId: string;                  // E1..E12 (operator cards later)
  inputs: Record<string, unknown>;     // values keyed by InputField.key (files kept as File)
  status: 'idle' | 'running' | 'done' | 'error';
  message?: string;
  producedType?: string;               // node type produced → suggested-next-step chips
  producedNodeId?: string;
  /** Set when the node is on the canvas but the case did not take it (UX02): why, and what to retry. */
  saveFailed?: { reason: string; items: { type: string; label: string; chain?: string }[] };
}

interface IntakeState {
  caseId?: string;                     // set on a real case (/cases/:id/canvas); undefined on the /fusion demo
  started: boolean;
  startedFrom?: string;
  subjectName: string;                 // the case subject's / suspect's name → labels the subject hub node
  cards: IntakeCard[];
  runLog: RunEvent[];
  replay: ReplayState | null;          // Increment C — press-play replay over the case history
  pendingNodes: PendingNode[];         // nodes produced while the canvas was unmounted → drained on mount
  userElements: PendingNode[];         // durable snapshot of the user's own canvas elements (nodes + edges),
                                       // captured on FusionCanvas unmount and restored on mount so "my data"
                                       // survives tab switches (the demo canvas mounts a fresh instance each time)
  setCaseId: (id?: string) => void;
  setSubjectName: (name: string) => void;
  startFrom: (templateId: string, cards: IntakeCard[]) => void;
  addCard: (card: IntakeCard) => void;
  removeCard: (id: string) => void;
  setInput: (cardId: string, key: string, value: unknown) => void;
  patchCard: (cardId: string, patch: Partial<IntakeCard>) => void;
  addRun: (e: RunEvent) => void;
  startReplay: (events: ReplayEntry[], opts?: { step?: number; playing?: boolean }) => void;
  setReplayStep: (n: number) => void;
  setReplayPlaying: (p: boolean) => void;
  stopReplay: () => void;
  enqueueNode: (data: PendingNode) => void;
  drainPendingNodes: () => PendingNode[];
  saveUserElements: (els: PendingNode[]) => void;
  reset: () => void;
}

export const useIntakeStore = create<IntakeState>((set, get) => ({
  caseId: undefined,
  started: false,
  subjectName: '',
  cards: [],
  runLog: [],
  replay: null,
  pendingNodes: [],
  userElements: [],
  setCaseId: (id) => set({ caseId: id }),
  setSubjectName: (name) => set({ subjectName: name }),
  startFrom: (templateId, cards) => set({ started: true, startedFrom: templateId, cards }),
  addCard: (card) => set((s) => ({ cards: [...s.cards, card] })),
  removeCard: (id) => set((s) => ({ cards: s.cards.filter((c) => c.id !== id) })),
  setInput: (cardId, key, value) => set((s) => ({ cards: s.cards.map((c) => (c.id === cardId ? { ...c, inputs: { ...c.inputs, [key]: value } } : c)) })),
  patchCard: (cardId, patch) => set((s) => ({ cards: s.cards.map((c) => (c.id === cardId ? { ...c, ...patch } : c)) })),
  addRun: (e) => set((s) => ({ runLog: [e, ...s.runLog].slice(0, 200) })),
  startReplay: (events, opts) => {
    if (!events.length) {
      set({ replay: null });
      return;
    }
    const step = Math.max(1, Math.min(events.length, opts?.step ?? 1));
    const playing = opts?.playing ?? true;
    set({ replay: { events, step, playing } });
  },
  setReplayStep: (n) => set((s) => {
    if (!s.replay) return {};
    const max = s.replay.events.length;
    return { replay: { ...s.replay, step: Math.max(1, Math.min(max, n)) } };
  }),
  setReplayPlaying: (p) => set((s) => (s.replay ? { replay: { ...s.replay, playing: p } } : {})),
  stopReplay: () => set({ replay: null }),
  enqueueNode: (data) => set((s) => ({ pendingNodes: [...s.pendingNodes, data] })),
  drainPendingNodes: () => { const q = get().pendingNodes; if (q.length) set({ pendingNodes: [] }); return q; },
  saveUserElements: (els) => set({ userElements: els }),
  reset: () => set({ started: false, startedFrom: undefined, subjectName: '', cards: [], runLog: [], replay: null, pendingNodes: [], userElements: [] }),
}));

let _seq = 0;
export const newIntakeId = (p = 'card') => `${p}-${Date.now()}-${_seq++}`;
